import { TextField, Stack, FormControlLabel, Checkbox, Divider } from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { CalendarRepeatComponent, isRRuleValid, rruleObjectToString, rruleStringToObject } from './CalendarRepeatComponent'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import useDateFormatter from '../../generic/hooks/useDateFormatter/useDateFormatter'

const CalendarEventMenu = (props) => {
  /**
   * @param {boolean} isJobLinkerEvent - If it`s true, disables the fields end date, end hour, name and working checkbox.
   */
  const { calendarEvent, onCalendarEventChange, showErrors, isJobLinkerEvent = false } = props
  const { t_ } = useSmartTranslation()
  const { format, ampm } = useDateFormatter()
  const nameError = (showErrors && (calendarEvent.name === null || calendarEvent.name.trim().length === 0))
  const dateError = ((calendarEvent.endDate !== null && calendarEvent.startDate >= calendarEvent.endDate) || calendarEvent.startDate == null)
  return (
    <Stack>
      {!isJobLinkerEvent
        ? (
          <TextField
            label={t_('Name')}
            variant='outlined'
            value={calendarEvent.name}
            error={nameError}
            helperText={nameError ? t_('Name cannot be empty') : null}
            onChange={(event) => { onCalendarEventChange({ ...calendarEvent, name: event.target.value }) }}
            disabled={calendarEvent.systemDefault}
            sx={{ marginTop: '1rem' }}
          />
          )
        : null}

      <Stack spacing={2} sx={{ marginTop: '1rem' }}>
        <div style={!isJobLinkerEvent
          ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gridGap: '1rem' }
          : undefined}
        >
          <DateTimePicker
            label={t_('Start date')}
            format={format.dateTime}
            ampm={ampm}
            value={calendarEvent.startDate}
            onChange={(newValue) => {
              if (isJobLinkerEvent) {
                onCalendarEventChange({
                  ...calendarEvent,
                  startDate: newValue
                })
              } else {
                onCalendarEventChange({ ...calendarEvent, startDate: newValue })
              }
            }}
            slotProps={{ textField: { error: dateError } }}
            disabled={calendarEvent.systemDefault}
          />
          {!isJobLinkerEvent
            ? (
              <DateTimePicker
                label={t_('End date')}
                value={calendarEvent.endDate}
                format={format.dateTime}
                ampm={ampm}
                onChange={(newValue) => {
                  onCalendarEventChange({ ...calendarEvent, endDate: newValue })
                }}
                slotProps={{ textField: { error: dateError, helperText: dateError ? t_('End date must be after start date') : null } }}
                disabled={calendarEvent.systemDefault}
              />)
            : null}
        </div>
        {!isJobLinkerEvent
          ? (
            <Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={calendarEvent.working}
                    disabled={calendarEvent.systemDefault}
                    onChange={(event, newValue) => {
                      onCalendarEventChange({ ...calendarEvent, working: newValue })
                    }}
                  />
            } label={t_('Working')}
              />
            </Stack>)
          : null}
        <Divider />
        <CalendarRepeatComponent
          rrule={calendarEvent.rrule}
          showErrors={showErrors}
          minDate={calendarEvent.endDate}
          onChange={(newValue, startDate) => {
            const dateStart = startDate || calendarEvent.startDate
            onCalendarEventChange({ ...calendarEvent, rrule: newValue, startDate: dateStart })
          }}
          disable={calendarEvent.systemDefault}
          isJobLinkerEvent={isJobLinkerEvent}
          calendarEvent={calendarEvent}
        />
      </Stack>
    </Stack>
  )
}

const eventToMutableEvent = (event) => {
  const startDate = new Date(event.startDate)
  const endDate = new Date(event.endDate)
  return { ...event, startDate, endDate, rrule: rruleStringToObject(event.rrule) }
}

const mutableEventToEvent = (mutableEvent) => {
  const event = { ...mutableEvent }
  event.rrule = rruleObjectToString(event.rrule)
  event.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return event
}

const isEventValid = (calendarEvent) => {
  if (calendarEvent.name === null || calendarEvent.name.trim().length === 0) {
    return false
  }
  if (calendarEvent.startDate >= calendarEvent.endDate) {
    return false
  }
  if (calendarEvent.rrule !== null && calendarEvent.rrule.ON && !isRRuleValid(calendarEvent.rrule, calendarEvent.endDate)) {
    return false
  }

  return true
}

export { CalendarEventMenu, eventToMutableEvent, mutableEventToEvent, isEventValid }
